import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';
import { applyWarehouseFilter, warehouseParamsSerializer, appendWarehouseParams } from '../utils/warehouseParams';
import purchaseOrderService from './purchaseOrderService';

// Axios instance for orders API
const ordersAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: warehouseParamsSerializer,
});

// Attach auth token if available
ordersAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) config.headers.Authorization = `Bearer ${token}`;
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure
ordersAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    // Skip logout on 401 for bulk-delete endpoints (which use 401 for password errors)
    if (err.response?.status === 401 && !err.config?.url?.includes('/bulk-delete/')) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user_data");
      sessionStorage.removeItem("auth_token");
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("user_data");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

const BASE_URL = '/orders';

// Caching helpers (memory + sessionStorage)
const CACHE_PREFIX = 'erp_orders_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

const memoryCache = new Map();

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (e) {
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    sessionStorage.setItem(cacheKey(key), JSON.stringify({ value, expires: Date.now() + ttl }));
  } catch (e) { }
}

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try { memoryCache.delete(key); } catch (e) { }
  try { sessionStorage.removeItem(cacheKey(key)); } catch (e) { }
}

function updateFullListCachesWithModifier(modifierFn) {
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === 'string' && key.startsWith('all::')) {
        const arr = entry?.value;
        if (Array.isArray(arr)) writeMemoryCache(key, modifierFn(arr.slice()));
      }
    }
  } catch (e) { }

  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const val = parsed?.value;
          if (Array.isArray(val)) {
            parsed.value = modifierFn(val.slice());
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) { }
      }
    }
  } catch (e) { }
}


const ordersService = {
  async getAllOrders(params = {}, options = {}) {
    const { force = false } = options;
    const key = `all::${JSON.stringify(params || {})}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }
    }

    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', String(params.skip));
      if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
      if (params.search !== undefined && params.search !== '') {
        queryParams.append('search', params.search);
      }
      if (params.from_date) queryParams.append('from_date', params.from_date);
      if (params.to_date) queryParams.append('to_date', params.to_date);
      if (params.status) {
        const statuses = Array.isArray(params.status) ? params.status : [params.status];
        statuses.forEach(s => queryParams.append('status', s));
      }
      if (params.gst_included !== undefined) {
        queryParams.append('gst_included', String(params.gst_included));
      }
      appendWarehouseParams(queryParams);

      const response = await ordersAPI.get(`${BASE_URL}?${queryParams.toString()}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getOrderById(orderId, options = {}) {
    const { force = false } = options;
    const key = `byId::${orderId}`;
    if (!force) {
      const mem = readMemoryCache(key);
      if (mem) return { success: true, data: mem };
      const sess = readCache(key);
      if (sess) { writeMemoryCache(key, sess); return { success: true, data: sess }; }

      // fallback: search in a cached full-list
      const fullKey = `all::${JSON.stringify({})}`;
      const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
      if (fullMem && Array.isArray(fullMem)) {
        const found = fullMem.find((i) => String(i.id) === String(orderId));
        if (found) { writeMemoryCache(key, found); writeCache(key, found); return { success: true, data: found }; }
      }
    }

    try {
      const response = await ordersAPI.get(`${BASE_URL}/${orderId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrder(orderPayload) {
    try {
      let response;

      if (orderPayload && typeof orderPayload === 'object' && orderPayload.customer) {
        response = await ordersAPI.post(`${BASE_URL}/new-customer`, orderPayload);
      } else if (orderPayload && (orderPayload.customer_id || orderPayload.customerId)) {
        response = await ordersAPI.post(`${BASE_URL}/existing-customer`, orderPayload);
      } else {
        response = await ordersAPI.post(BASE_URL, orderPayload);
      }

      const created = response.data;

      // prepend to cached full-lists
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });

      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }

      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForNewCustomer(payload) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/new-customer`, payload);
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForExistingCustomer(payload) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/existing-customer`, payload);
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForExistingCustomerWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await ordersAPI.post(`${BASE_URL}/existing-customer/with-attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async createOrderForNewCustomerWithAttachments(payload, files = []) {
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      files.forEach(f => fd.append('files', f));
      const response = await ordersAPI.post(`${BASE_URL}/new-customer/with-attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = response.data;
      updateFullListCachesWithModifier((arr) => {
        const exists = arr.some((i) => String(i.id) === String(created.id));
        if (exists) return arr;
        return [created, ...arr];
      });
      try { const key = `byId::${created.id}`; writeMemoryCache(key, created); writeCache(key, created); } catch (e) { }
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async updateOrder(orderId, orderPayload) {
    try {
      const response = await ordersAPI.put(`${BASE_URL}/${orderId}`, orderPayload);
      const updated = response.data;

      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));

      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async confirmOrder(orderId) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/confirm`);
      const updated = response.data;

      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async deleteOrder(orderId) {
    try {
      const response = await ordersAPI.delete(`${BASE_URL}/${orderId}`);
      updateFullListCachesWithModifier((arr) => arr.filter((i) => String(i.id) !== String(orderId)));
      try { deleteCacheEntry(`byId::${orderId}`); } catch (e) { }
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async cancelOrder(orderId) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/cancel`);
      const updated = response.data;
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async bulkDeleteOrders(orderIds = []) {
    try {
      const ids = (orderIds || []).map((id) => parseInt(id, 10)).filter((id) => !isNaN(id));
      const response = await ordersAPI.post(`${BASE_URL}/bulk-delete`, ids);

      const idSet = new Set(ids.map((id) => String(id)));
      updateFullListCachesWithModifier((arr) => arr.filter((i) => !idSet.has(String(i.id))));
      try { for (const id of ids) deleteCacheEntry(`byId::${id}`); } catch (e) { }

      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // Delivery-related endpoints (proxying delivery-orders routes here)
  async assignPersonnelToDelivery(orderId, payload = {}) {
    try {
      const response = await ordersAPI.post(`/delivery-orders/${orderId}/assign-personnel`, payload);
      const updated = response.data;
      // update caches / full-lists if present
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async loadTruckForDelivery(orderId, payload = {}) {
    try {
      const response = await ordersAPI.post(`/delivery-orders/${orderId}/load-truck`, payload);
      const updated = response.data;
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async completeDeliveryLoading(orderId, payload = {}) {
    try {
      const response = await ordersAPI.post(`/delivery-orders/${orderId}/complete-loading`, payload);
      const updated = response.data;
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async saveLoadingDraft(orderId, payload = {}) {
    try {
      const response = await ordersAPI.put(`/delivery-orders/${orderId}/save-loading-draft`, payload);
      const updated = response.data;
      updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? updated : i)));
      try { const key = `byId::${orderId}`; writeMemoryCache(key, updated); writeCache(key, updated); } catch (e) { }
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async markDeliveryChallanDelivered(challanId, payload = {}) {
    try {
      const response = await ordersAPI.post(`/delivery-challans/${challanId}/mark-delivered`, payload);
      const updated = response.data;

      // Update caches if API returned an updated object (best-effort)
      try {
        if (updated && updated.id) {
          const key = `byId::${updated.id}`;
          writeMemoryCache(key, updated);
          writeCache(key, updated);
          updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(updated.id) ? updated : i)));
        }
      } catch (e) { }

      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async generateDeliveryChallan(challanId, payload = {}) {
    try {
      // Ensure challan_date exists in YYYY-MM-DD format; default to today's date
      const today = new Date().toISOString().slice(0, 10);
      const body = { ...payload, challan_date: payload.challan_date ?? today };
      const response = await ordersAPI.post(`/delivery-challans/${challanId}/generate`, body);
      const data = response.data;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async generateInvoice(orderId, payload = {}) {
    try {
      // Ensure invoice_date exists in YYYY-MM-DD format; default to today's date
      const today = new Date().toISOString().slice(0, 10);
      const body = { ...payload, invoice_date: payload.invoice_date ?? today };
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/generate-invoice`, body);
      const data = response.data;

      // If the API returns an updated order object, update caches for consistency
      try {
        if (data && data.id && String(data.id) === String(orderId)) {
          const key = `byId::${orderId}`;
          writeMemoryCache(key, data);
          writeCache(key, data);
          updateFullListCachesWithModifier((arr) => arr.map((i) => (String(i.id) === String(orderId) ? data : i)));
        }
      } catch (e) { }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  // Delivery Challans CRUD
  async getAllDeliveryChallans(params = {}, options = {}) {
    try {
      const response = await ordersAPI.get('/delivery-challans', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getDeliveryChallanById(id) {
    try {
      const response = await ordersAPI.get(`/delivery-challans/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async updateDeliveryChallan(id, payload = {}) {
    try {
      const response = await ordersAPI.put(`/delivery-challans/${id}`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  /**
   * Delete ALL orders across Sales and Purchase modules.
   * Requires super admin password.
   */
  async bulkDeleteAllOrdersGlobal(payload) {
      try {
          const response = await ordersAPI.post('/bulk-delete/all-orders', payload);
          if (response.data?.success || response.status === 200) {
              this.clearCache();
              purchaseOrderService.clearCache();
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  /**
   * Delete ALL Sales Orders (delivery orders, sub-orders, items, challans).
   * Requires super admin password.
   */
  async bulkDeleteSalesOrders(payload) {
      try {
          const response = await ordersAPI.post('/bulk-delete/sales-orders', payload);
          if (response.data?.success || response.status === 200) {
              this.clearCache();
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  /**
   * Delete ALL Purchase Orders (advices, advice items, challans).
   * Requires super admin password.
   */
  async bulkDeletePurchaseOrdersGlobal(payload) {
      try {
          const response = await ordersAPI.post('/bulk-delete/purchase-orders', payload);
          if (response.data?.success || response.status === 200) {
              purchaseOrderService.clearCache();
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  /**
   * Delete specific Sales and/or Purchase orders by ID.
   * Payload: { password, order_ids: [], purchase_order_ids: [] }
   * Requires super admin password.
   */
  async bulkDeleteSelectedOrders(payload) {
      try {
          const response = await ordersAPI.post('/bulk-delete/selected-orders', payload);
          if (response.data?.success || response.status === 200) {
              if (payload.order_ids?.length) this.clearCache();
              if (payload.purchase_order_ids?.length) purchaseOrderService.clearCache();
          }
          return { success: true, ...response.data };
      } catch (error) {
          return { success: false, error: extractApiError(error) };
      }
  },

  async sendOrderEmail(orderId) {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/send-email`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async sendOrderNotification(orderId, type = 'email') {
    try {
      const response = await ordersAPI.post(`${BASE_URL}/${orderId}/send-notification`, null, { params: { type } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async sendChallanEmail(subOrderId) {
    try {
      const response = await ordersAPI.post(`/delivery-sub-orders/${subOrderId}/send-challan-email`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async sendChallanNotification(subOrderId, type = 'email') {
    try {
      const response = await ordersAPI.post(`/delivery-sub-orders/${subOrderId}/send-challan-notification`, null, { params: { type } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async getVehicleTracking(params = {}) {
    try {
      const response = await ordersAPI.get('/vehicle-tracking', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  async exportDeliveryChallans(params = {}) {
    try {
      const response = await ordersAPI.get('/delivery-challans/export/download', {
        params,
        responseType: 'blob',
      });
      return { success: true, data: response.data, headers: response.headers };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  },

  clearCache() {
    try {
      for (const [key] of memoryCache.entries()) {
        if (typeof key === 'string' && key.includes('orders')) memoryCache.delete(key);
      }
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
      }
    } catch (e) { }
  }
};

export default ordersService;
